"use client";

import React, { useState } from "react";
import Header from "@/components/layout/Header";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";

export default function PricingPageContent() {
	const [isUpgrading, setIsUpgrading] = useState(false);

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	return (
		<div className="min-h-screen bg-[#16251c]">
			<Header />
			<main
				className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20"
				style={{ marginTop: "10rem" }}
			>
				{/* SEO Heading - Hidden visually but accessible */}
				<h1 className="sr-only">
					Tax Lien Vest Pricing - Professional Plan
				</h1>

				<div className="flex flex-col items-center justify-center text-center mb-16">
					<div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-sm border border-white/10">
						<Sparkles className="w-8 h-8 text-[#A7FF5E]" />
					</div>
					<h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
						Simple, Transparent Pricing
					</h2>
					<p className="text-[#8c99a7] text-lg md:text-xl max-w-2xl leading-relaxed">
						Join thousands of smart investors who use our
						high-accuracy property intelligence to make ROI-driven
						decisions.
					</p>
				</div>

				<div className="max-w-md mx-auto">
					<div className="bg-[#1a2c20] rounded-3xl p-8 md:p-10 border border-white/10 shadow-2xl relative overflow-hidden">
						{/* Background glow */}
						<div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-[#A7FF5E] opacity-5 blur-[100px] rounded-full pointer-events-none"></div>

						<div className="flex flex-col items-center mb-8">
							<span className="text-[#edffde] font-medium uppercase tracking-widest text-sm mb-4">
								Professional Plan
							</span>
							<div className="flex items-baseline text-white">
								<span className="text-6xl font-bold text-[#A7FF5E]">
									$49
								</span>
								<span className="text-xl text-[#8c99a7] ml-2 font-medium">
									/one-time
								</span>
							</div>
							<p className="text-[#8c99a7] mt-4 text-sm">
								No monthly fees. Lifetime access to current
								features.
							</p>
						</div>

						<div className="space-y-6 mb-10">
							<div className="flex items-start gap-3">
								<div className="w-6 h-6 rounded-full bg-[#A7FF5E]/20 flex items-center justify-center shrink-0 mt-0.5">
									<Check className="w-3.5 h-3.5 text-[#A7FF5E]" />
								</div>
								<p className="text-gray-300 text-sm leading-relaxed">
									<span className="text-white font-semibold">
										High Accuracy Data
									</span>{" "}
									- Verified financial and legal records.
								</p>
							</div>
							<div className="flex items-start gap-3">
								<div className="w-6 h-6 rounded-full bg-[#A7FF5E]/20 flex items-center justify-center shrink-0 mt-0.5">
									<Check className="w-3.5 h-3.5 text-[#A7FF5E]" />
								</div>
								<p className="text-gray-300 text-sm leading-relaxed">
									<span className="text-white font-semibold">
										Authentic Parcel Data
									</span>{" "}
									- Detailed property characteristics.
								</p>
							</div>
							<div className="flex items-start gap-3">
								<div className="w-6 h-6 rounded-full bg-[#A7FF5E]/20 flex items-center justify-center shrink-0 mt-0.5">
									<Check className="w-3.5 h-3.5 text-[#A7FF5E]" />
								</div>
								<p className="text-gray-300 text-sm leading-relaxed">
									<span className="text-white font-semibold">
										Fast Property Insights
									</span>{" "}
									- Export directly to Google Maps.
								</p>
							</div>
							<div className="flex items-start gap-3">
								<div className="w-6 h-6 rounded-full bg-[#A7FF5E]/20 flex items-center justify-center shrink-0 mt-0.5">
									<Check className="w-3.5 h-3.5 text-[#A7FF5E]" />
								</div>
								<p className="text-gray-300 text-sm leading-relaxed">
									<span className="text-white font-semibold">
										First Movers Advantage
									</span>{" "}
									- Get ahead in lien property investing.
								</p>
							</div>
						</div>

						<button
							onClick={handleUpgrade}
							disabled={isUpgrading}
							className="w-full bg-[#A7FF5E] hover:bg-[#8ee63f] text-[#16251c] font-bold py-4 px-6 rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
						>
							{isUpgrading ? (
								<>
									<Loader2 className="w-5 h-5 animate-spin" />
									<span>Processing...</span>
								</>
							) : (
								<span>Get Started Now</span>
							)}
						</button>

						<p className="text-center text-[#8c99a7] text-xs mt-6">
							Secure payment powered by Stripe
						</p>
					</div>
				</div>
			</main>
		</div>
	);
}
